export const ModuleData = {
  Module1: {
    "Module Description": {
      type: "text",
      value:
        "This video module provides a comprehensive introduction to the world of artificial intelligence (AI), covering fundamental concepts including machine learning, deep learning, neural networks, and their applications in real-world scenarios, particularly in agriculture. Through engaging content and practical examples, students will gain a deeper understanding of AI's capabilities, challenges, and its transformative potential in various sectors.",
    },
    "Learning Objectives": {
      type: "list",
      value: [
        "Gain a better understanding of artificial intelligence (AI)",
        "Understand the use of machine learning and deep learning",
        "Understand how a convolutional neural network works",
        "Learn how computer vision is applied to solve real-world problems",
        "Address AI bias",
        "Learn various applications of AI in agriculture",
      ],
    },
    References: {
      type: "references",
      value: [
        "IBM. (2023a). What are Convolutional Neural Networks? IBM. https://www.ibm.com/topics/convolutionalneural-networks",
        "IBM. (2023b). What is artificial intelligence (AI)? IBM. https://www.ibm.com/topics/artificial-intelligence",
        "IBM. (2023c). Shedding light on AI bias with real world examples. IBM. https://www.ibm.com/blog/shedding-lighton-ai-bias-with-real-world-examples/",
        "Long, D., & Magerko, B. (2020). What is AI Literacy? Competencies and Design Considerations. Proceedings of the 2020 CHI Conference on Human Factors in Computing Systems, 1–16. https://doi.org/10.1145/3313831.3376727",
        "Proxima. (2023, May 10). Addressing Bias in AI for Medical Applications: A Comprehensive Guide. Proximacro. https://www.proximacro.com/news/addressing-bias-in-ai-for-medical-applications-a-comprehensive-guide ",
      ],
    },
    "Image References": {
      type: "image_references",
      value: [
        "Culmbio. (2024). Agriculture free icons [PNG Icon]. Flaticon.com. https://www.flaticon.com/free-icon/agriculture_5994257",
        "i2tutorials. (2024). What is the difference between Artificial Intelligence, Machine Learning and Deep Learning? [Image]. i2tutorials.com. https://www.i2tutorials.com/what-is-the-difference-between-artificial-intelligence-machine-learning-and-deep-learning/",
        "Orchi, H., Sadik, M., & Khaldoun, M. (2022). Graphical Abstract [Image]. Multidisciplinary Digital Publishing Institute. https://www.mdpi.com/2077-0472/12/1/9",
        "SGU Public Relation. (2020). Computer Vision Animation [GIF]. Swiss German University. https://sgu.ac.id/id/computer-vision-artificial-intelligence-why-is-it-important/",
      ],
    },
  },
  Module2: {
    "Link to Dataset": {
      type: "link",
      value:
        "https://drive.google.com/drive/folders/10eOpcpC4Ovt4nmfkzcQb0hS79_r7EF7l?usp=drive_link",
      link_name: "AI-Lit Crop Disease Dataset",
    },
    "Module Description": {
      type: "text",
      value:
        "This module dives into the practical aspects of machine learning, focusing on crop disease detection using image data. Students will learn how to acquire, organize, and preprocess image datasets, set up a Google Colab environment for machine learning, and build a basic Convolutional Neural Network (CNN) model. Using Python libraries like TensorFlow, Keras, NumPy, and Matplotlib, learners will gain hands-on experience in preparing data and developing a model that can effectively diagnose plant disease.",
    },
    "Learning Objectives": {
      type: "list",
      value: [
        "Explore machine learning applications in crop disease detection",
        "Acquire, organize, and preprocess an image dataset",
        "Set up Google Colab for machine learning development",
        "Build and compile a basic CNN model.",
        "Utilize Python libraries (TensorFlow, Keras, NumPy, Matplotlib)",
      ],
    },
    "Coding Exercises": {
      type: "list",
      value: [
        "Exercise 2.1: Import necessary Python libraries and understand their functions in the context of machine learning and data visualization.",
        "Exercise 2.2: Mount Google Drive in Colab and understand the process of accessing and organizing datasets.",
        "Exercise 2.3: Learn about the importance of dividing the dataset into training, validation, and testing sets, and how to preprocess and organize the images for the CNN model.",
        "Exercise 2.4: Visualize and analyze the dataset using Matplotlib to understand the characteristics of different categories of leaves.",
      ],
    },
    References: {
      type: "references",
      value: [
        "Blight—Plant Pathology. (2024). Britannica. https://www.britannica.com/science/blight",
        "Intel. (2023). What Is a GPU? Graphics Processing Units Defined. Intel. https://www.intel.com/content/www/us/en/products/docs/processors/what-is-a-gpu.html",
        "Keras. (2024). Keras. https://keras.io/",
        "Matplotlib Development Team. (2024). Matplotlib—Visualization with Python. Matplotlib. https://matplotlib.org/",
        "Numpy Team. (2024). Numpy. Numpy. https://numpy.org/ ",
        "Schuh, M., & Grabowski, M. (2023). Early blight in tomato and potato. University of Minnesota Extension. https://extension.umn.edu/disease-management/early-blight-tomato-and-potato",
        "Schuh, M., & Grabowski, M. (2021). Late blight of tomato and potato. University of Minnesota Extension. https://extension.umn.edu/disease-management/late-blight",
        "Scikit-Learn. (2024). Scikit-Learn: Machine Learning in Python. https://scikit-learn.org/stable/",
        "Shah, T. (2020, July 10). About Train, Validation and Test Sets in Machine Learning. Medium. https://towardsdatascience.com/train-validation-and-test-sets-72cb40cba9e7",
        "Shutil. (2024). Shutil—High-level file operations. Python Documentation. Retrieved April 10, 2024, from https://docs.python.org/3/library/shutil.html",
        "Simic, M., & Aibin, M. (2023, May 5). How to Analyze Loss vs. Epoch Graphs? https://www.baeldung.com/cs/loss-vs-epoch-graphs",
        "Tejaswani, A. (2020). PlantVillage Dataset. Kaggle. https://www.kaggle.com/datasets/emmarex/plantdisease",
        "TensorFlow. (2024). TensorFlow. TensorFlow. Retrieved April 10, 2024, from https://www.tensorflow.org/",
        "Waskom, M. (2024). Seaborn: Statistical Data Visualization. Journal of Open Source Software. seaborn.pydata.org",
      ],
    },
    "Image References": {
      type: "image_references",
      value: [
        "Keras. (2024). Keras [Logo]. https://keras.io/",
        "Matplotlib Development Team. (2024). Matplotlib—Visualization with Python [Logo]. https://matplotlib.org/",
        "Numpy Team. (2024). Numpy [Logo]. Numpy. https://numpy.org/",
        "Scikit-Learn. (2024). Scikit-Learn: Machine Learning in Python [Logo]. https://scikit-learn.org/stable/",
        "Shutil. (2024). Shutil—High-level file operations [Logo]. Python Documentation. Retrieved April 10, 2024, from https://docs.python.org/3/library/shutil.html",
        "Tejaswani, A. (2020). PlantVillage Dataset Images [Image]. Kaggle. https://www.kaggle.com/datasets/emmarex/plantdisease",
        "TensorFlow. (2024). TensorFlow [Logo]. TensorFlow. Retrieved April 10, 2024, from https://www.tensorflow.org/",
        "Waskom, M. (2024). Seaborn: Statistical Data Visualization [Logo]. Journal of Open Source Software. seaborn.pydata.org",
      ],
    },
  },
  Module3: {
    "Module Description": {
      type: "text",
      value:
        "Module 3 focuses on the practical aspects of training a machine learning model, specifically for crop disease detection. Students will learn how to construct a neural network using pre-trained models and custom layers, and then train this network to classify images of tomato leaves based on their health status. The module introduces concepts such as activation functions, parameters, and the importance of layer structure, providing a deeper understanding of how neural networks operate.",
    },
    "Learning Objectives": {
      type: "list",
      value: [
        "Understand CNN model structures and variables",
        "Learn how to use and enhance pre-trained deep learning models",
        "Learn how to add custom layers to a pre-trained MobileNetV2 model for specific disease identification",
        "Learn how to compile AI models with appropriate optimizers and loss functions, and execute model training",
        "Explore adjusting training parameters (epochs) to optimize model performance",
      ],
    },
    "Coding Exercises": {
      type: "list",
      value: [
        "Exercise 3.1: Loading the pre-trained MobileNetV2 model, setting it up for feature extraction, and understanding its structure and functionality.",
        "Exercise 3.2: Demonstrating how to add custom layers to the pre-trained model to tailor it for crop disease classification, including global average pooling and dense layers with softmax activation for multiclass classification.",
        "Exercise 3.3: Configuring the model for training using the Adam optimizer and categorical cross-entropy loss function. Training the model while evaluating its performance on training and validation data sets.",
      ],
    },
    References: {
      type: "references",
      value: [
        "Bnomial. (2024). Overfitting and Underfitting with Learning Curves. Binomial. https://articles.bnomial.com/overfitting-underfitting-learning-curves",
        "Evidently AI. (2024). Classification Metrics Guide. Evidently AI https://www.evidentlyai.com/classification-metrics/accuracy-precision-recall",
        "Ponomarenko A., Goodwin, S.B. and Kema, G.H.J. (2011). Septoria tritici blotch (STB) of wheat. Plant Health Instructor. https://doi.org/10.1094/PHI-I-2011-0407-01",
        "Salgado, J. D., & Paul, P. A. (2016, April 8). Leaf Blotch Diseases of Wheat—Septoria tritici Blotch, Stagonospora nodorum Blotch and Tan Spot. Agriculture and Natural Resources. https://ohioline.osu.edu/factsheet/plpath-cer-07",
        "Simplilearn. (2022, August 30). What is Epoch in Machine Learning? Simplilearn. https://www.simplilearn.com/tutorials/machine-learning-tutorial/what-is-epoch-in-machine-learning",
        "Tf.keras.applications.MobileNetV2 | TensorFlow v2.16.1. (2024). TensorFlow. https://www.tensorflow.org/api_docs/python/tf/keras/applications/MobileNetV2",
      ],
    },
    "Image References": {
      type: "image_references",
      value: [
        "Akay, M., Du, Y., Sershen, C. L., Wu, M., Chen, T. Y., Assassi, S., Mohan, C., & Akay, Y. M. (2021). Deep Learning Classification of Systemic Sclerosis Skin Using the MobileNetV2 Model [Image]. Open Journal of Engineering in Medicine and Biology. https://ieeexplore.ieee.org/document/9380371",
        "Spence, M. (2023). Software Architecture Blueprint [Image]. Medium.com. https://medium.com/@msaspence/what-is-software-architecture-71355510b4d0",
        "Tejaswani, A. (2020). PlantVillage Septoria [Image]. Kaggle. https://www.kaggle.com/datasets/emmarex/plantdisease",
      ],
    },
  },
  Module4: {
    "Module Description": {
      type: "text",
      value:
        "Module 4 delves into evaluating the performance of the trained machine learning model, focusing on understanding and applying accuracy metrics and testing the model on new, unseen data. Participants will learn to interpret loss graphs to assess model fit and explore various metrics to gauge the model's predictive power accurately.",
    },
    "Learning Objectives": {
      type: "list",
      value: [
        "Understand how to plot and interpret loss graphs to guide model tuning",
        "Evaluate model on a test set to simulate real-world performance and ensure unbiased evaluation",
        "Use the model to analyze individual images, identifying the model's practical application and accuracy",
        "Gain an understanding of key performance metrics like precision, recall, F1-score, and support, and their importance in model evaluation",
        "Learn how to generate and interpret a classification report and a confusion matrix to evaluate the model's performance ",
      ],
    },
    "Coding Exercises": {
      type: "list",
      value: [
        "Exercise 4.1: How to visualize training and validation loss to assess model performance by plotting loss against epochs, with discussions on identifying signs of underfitting and overfitting.",
        "Exercise 4.2: Techniques for using the test set to measure the model's accuracy and loss, ensuring unbiased evaluation of its real-world applicability.",
        "Exercise 4.3: Detailed explanation of precision, recall, F1-score, support, and how to generate a classification report to analyze the model's performance across different classes.",
        "Exercise 4.4: Developing and understanding the applicability of a confusion matrix to visualize model performance.",
      ],
    },
    References: {
      type: "references",
      value: [
        "Bnomial. (2024). Overfitting and Underfitting with Learning Curves. Binomial. https://articles.bnomial.com/overfitting-underfitting-learning-curves",
        "Evidently AI. (2024). Classification Metrics Guide. Evidently AI https://www.evidentlyai.com/classification-metrics/accuracy-precision-recall",
        "Mosaic—Description, Symptoms, & Control. (2024). Britannica. https://www.britannica.com/science/mosaic-plant-disease",
        "Schuh, M., Johnson, A., Grabowski, M., & Orshinsky, A. (2021). Tomato viruses. https://extension.umn.edu/disease-management/tomato-viruses",
      ],
    },
    "Image References": {
      type: "image_references",
      value: [
        "Alamy Limited. (2024.). Large school building scene illustration [PNG Icon]. https://www.alamy.com/large-school-building-scene-illustration-image260635393.html",
        "Bnomial. (2024). Overfitting and Underfitting with Learning Curves [Image]. Binomial.com. https://articles.bnomial.com/overfitting-underfitting-learning-curves",
        "Lay, G. K. (2024). School Examination Icon [PNG Icon]. https://thenounproject.com/icon/school-examination-1245419/",
        "Tejaswani, A. (2020). PlantVillage Blight [Image]. Kaggle. https://www.kaggle.com/datasets/emmarex/plantdisease",
      ],
    },
  },
};
